<c-container fluid>
  <c-row>
    <c-col xs="12">
      <c-card class="mb-4">
        <c-card-header>
          <strong>{{ 'admin.users.title' | translate }}</strong>
          <small class="ms-2 text-body-secondary">{{ getTotalUsersText() }}</small>
        </c-card-header>

        <c-card-body>
          <!-- Filters Row -->
          <c-row class="mb-3">
            <c-col xs="12" md="3">
              <label for="userStatusFilter" class="form-label">{{ 'admin.users.userStatus' | translate }}</label>
              <select
                id="userStatusFilter"
                cSelect
                [ngModel]="userStatusFilter()"
                (ngModelChange)="onUserStatusFilterChange($event)"
                class="form-select"
              >
                <option value="all">{{ 'admin.users.all' | translate }}</option>
                <option value="active">{{ 'admin.users.active' | translate }}</option>
                <option value="inactive">{{ 'admin.users.inactive' | translate }}</option>
                <option value="unconfirmed">{{ 'admin.users.unconfirmed' | translate }}</option>
                <option value="deleted">{{ 'admin.users.deleted' | translate }}</option>
              </select>
            </c-col>

            <c-col xs="12" md="2">
              <label for="foundingMemberFilter" class="form-label">{{ 'founding_member.title' | translate }}</label>
              <select
                id="foundingMemberFilter"
                cSelect
                [ngModel]="foundingMemberFilter()"
                (ngModelChange)="onFoundingMemberFilterChange($event)"
                class="form-select"
              >
                <option value="all">{{ 'admin.users.all' | translate }}</option>
                <option value="yes">{{ 'admin.users.statusYes' | translate }}</option>
                <option value="no">{{ 'admin.users.statusNo' | translate }}</option>
              </select>
            </c-col>

            <c-col xs="12" md="4">
              <label class="form-label">{{ 'admin.users.createdAtLabel' | translate }}</label>
              <div class="d-flex gap-2">
                <c-date-range-picker
                  class="flex-grow-1"
                  [placeholder]="'admin.users.selectDateRange' | translate"
                  [range]="true"
                  [ranges]="dateRanges()"
                  [cleaner]="true"
                  [closeOnSelect]="true"
                  (valueChange)="onDateRangeChange('created_at', $event)"
                />
              </div>
            </c-col>

            <c-col xs="12" md="4">
              <label class="form-label">{{ 'admin.users.dateOfJoiningLabel' | translate }}</label>
              <div class="d-flex gap-2">
                <c-date-range-picker
                  class="flex-grow-1"
                  [placeholder]="'admin.users.selectDateRange' | translate"
                  [range]="true"
                  [ranges]="dateRanges()"
                  [cleaner]="true"
                  [closeOnSelect]="true"
                  (valueChange)="onDateRangeChange('date_of_joining', $event)"
                />
              </div>
            </c-col>
          </c-row>

          <!-- SmartTable -->
          <c-smart-table
            [columns]="columns()"
            [items]="users()"
            [header]="true"
            [columnFilter]="columnFilterConfig"
            [columnSorter]="columnSorterConfig"
            [itemsPerPageSelect]="itemsPerPageSelectConfig"
            [itemsPerPage]="itemsPerPage()"
            [itemsPerPageOptions]="[20, 50, 100, 10000]"
            [pagination]="false"
            (sorterValueChange)="onSorterChange($event)"
            (columnFilterValueChange)="onColumnFilterChange($event)"
            (itemsPerPageChange)="onItemsPerPageChange($event)"
          >
            <ng-template cTemplateId="tableData" let-columnName="columnName" let-item="item" let-tdContent="tdContent">
              <td [ngSwitch]="columnName">
                <div *ngSwitchCase="'actions'" class="text-nowrap">
                  <div class="flex">
                    <button
                      type="button"
                      cButton
                      color="link"
                      size="sm"
                      class="p-0"
                      (click)="openEditModal(item._rawId)"
                      title="Edit user"
                    >
                      <svg cIcon name="cilPencil" size="sm"></svg>
                    </button>
                    @if (communityEnabled() && item._rawId !== currentUser()?.id) {
                      <button
                        type="button"
                        cButton
                        color="link"
                        size="sm"
                        class="ms-1 p-0 text-danger"
                        (click)="openCommunitySanction(item._rawId)"
                        [title]="'admin.users.communitySanction' | translate"
                        [attr.aria-label]="'admin.users.communitySanction' | translate"
                      >
                        <svg cIcon name="cilBan" size="sm"></svg>
                      </button>
                    }
                    @if (isSuperAdmin() && item._rawId !== currentUser()?.id) {
                      <button
                        type="button"
                        cButton
                        color="link"
                        size="sm"
                        class="ms-1 p-0 text-warning"
                        (click)="impersonateUser(item._rawId)"
                        title="Impersonate user"
                      >
                        <svg cIcon name="cilUserFollow" size="sm"></svg>
                      </button>
                      @if (item._isDeleted) {
                        <!-- Restore button (only for deleted users) -->
                        <button
                        type="button"
                        cButton
                        color="link"
                        size="sm"
                        class="ms-1 p-0 text-success"
                        (click)="restoreUser(item._rawId)"
                        [title]="'admin.users.restoreUser' | translate"
                        >
                          <svg cIcon name="cilActionUndo" size="sm"></svg>
                        </button>
                      } @else {
                        <button
                        type="button"
                        cButton
                        color="link"
                        size="sm"
                        class="ms-1 p-0 text-danger"
                        (click)="openDeleteConfirmModal(item)"
                        [title]="'admin.users.softDelete' | translate"
                        >
                          <svg cIcon name="cilTrash" size="sm"></svg>
                        </button>
                      }
                    }
                  </div>
                </div>
                <div *ngSwitchCase="'balance'">
                  <button
                    type="button"
                    cButton
                    color="link"
                    size="sm"
                    class="p-0 fw-bold text-decoration-none"
                    (click)="toggleBalanceDetails(item._rawId)"
                    [disabled]="!item._balanceUsd"
                  >
                    {{ item._balanceUsd ? formatUsd(item._balanceUsd) : '-' }}
                    @if (item._balanceUsd) {
                      <span class="ms-1">{{ isBalanceExpanded(item._rawId) ? '▴' : '▾' }}</span>
                    }
                  </button>
                  @if (isBalanceExpanded(item._rawId)) {
                    <div class="small text-body-secondary mt-1">
                      @for (b of item._balances; track b.currency) {
                        <div class="d-flex justify-content-between gap-3">
                          <span>{{ formatAmount(b.balance, b.currency) }}</span>
                          <span class="text-nowrap">≈ {{ formatUsd(b.balance_usd) }}</span>
                        </div>
                      }
                    </div>
                  }
                </div>
                <div *ngSwitchCase="'domains'">
                  <span class="fw-semibold">{{ item._registrationDomain || '—' }}</span>
                  @if (item._domainVisits?.length) {
                    <button
                      type="button"
                      cButton
                      color="link"
                      size="sm"
                      class="p-0 ms-1 text-decoration-none"
                      (click)="toggleDomainDetails(item._rawId)"
                    >
                      {{ isDomainExpanded(item._rawId) ? '▴' : '▾' }}
                    </button>
                    @if (isDomainExpanded(item._rawId)) {
                      <div class="small text-body-secondary mt-1">
                        @for (d of item._domainVisits; track d.config_domain_id) {
                          <div class="d-flex justify-content-between gap-3">
                            <span>{{ d.name }}</span>
                            <span class="text-nowrap">{{ d.visit_count }}× · {{ d.last_seen_at | date:'short' }}</span>
                          </div>
                        }
                      </div>
                    }
                  }
                </div>
                <!-- Founding Member: the permanent honour for the first 1.000
                     paying Health Club members, with its number. -->
                <div *ngSwitchCase="'founding_member_number'" class="text-nowrap">
                  @if (item.founding_member_number) {
                    <founding-member-badge compact
                                           [foundingMember]="true"
                                           [memberNumber]="item.founding_member_number" />
                    <span class="ms-1 fw-semibold">#{{ item.founding_member_number }}</span>
                  }
                  <!-- Founding Practitioner sits in the same cell: both honours can
                       be held, and the column is about the founding honours. -->
                  @if (item.founding_practitioner_number) {
                    <founding-member-badge compact kind="practitioner"
                                           [foundingMember]="true"
                                           [memberNumber]="item.founding_practitioner_number" />
                    <span class="ms-1 fw-semibold">#{{ item.founding_practitioner_number }}</span>
                  }
                  @if (!item.founding_member_number && !item.founding_practitioner_number) {
                    <span class="text-body-secondary">—</span>
                  }
                </div>
                <!-- Status plus the community sanction in force (write block, ban, hide). -->
                <div *ngSwitchCase="'user_status'">
                  {{ tdContent }}
                  @if (item.community_sanction; as sanction) {
                    <div>
                      <span class="badge {{ communitySanctionClass(sanction) }}"
                            [title]="communitySanctionTooltip(sanction)">{{ communitySanctionLabel(sanction) }}</span>
                    </div>
                  }
                </div>
                <div *ngSwitchDefault>
                  {{ tdContent }}
                </div>
              </td>
            </ng-template>
          </c-smart-table>

          <!-- Custom Pagination -->
          @if (totalPages() > 0) {
            <c-row class="mt-3 align-items-center">
              <c-col xs="12" md="6">
                <div class="text-body-secondary">
                  {{ getShowingEntriesText() }}
                </div>
              </c-col>

              <c-col xs="12" md="6">
                <nav aria-label="Page navigation">
                  <ul class="pagination justify-content-end mb-0">
                    <li class="page-item" [class.disabled]="currentPage() === 1">
                      <button
                        class="page-link"
                        (click)="onActivePageChange(currentPage() - 1)"
                        [disabled]="currentPage() === 1"
                      >
                        {{ 'admin.users.previous' | translate }}
                      </button>
                    </li>
                    @for (page of getPageNumbers(); track page) {
                      <li class="page-item" [class.active]="page === currentPage()">
                        <button class="page-link" (click)="onActivePageChange(page)">
                          {{ page }}
                        </button>
                      </li>
                    }
                    <li class="page-item" [class.disabled]="currentPage() === totalPages()">
                      <button
                        class="page-link"
                        (click)="onActivePageChange(currentPage() + 1)"
                        [disabled]="currentPage() === totalPages()"
                      >
                        {{ 'admin.users.next' | translate }}
                      </button>
                    </li>
                  </ul>
                </nav>
              </c-col>
            </c-row>
          }
        </c-card-body>
      </c-card>
    </c-col>
  </c-row>
</c-container>

<!-- Payout API Tokens Section (Super Admin only) -->
@if (isSuperAdmin()) {
  <c-container fluid>
    <c-row>
      <c-col xs="12">
        <c-card class="mb-4">
          <c-card-header class="d-flex justify-content-between align-items-center">
            <div>
              <strong>Payout API Tokens</strong>
              <small class="ms-2 text-body-secondary">Generate tokens for CLI/curl payout access</small>
            </div>
            <button cButton color="primary" size="sm" (click)="openGenerateTokenModal()">
              Generate Token
            </button>
          </c-card-header>
          <c-card-body>
            <div class="table-responsive">
              <table class="table table-sm table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Created By</th>
                    <th>Description</th>
                    <th>Expires</th>
                    <th>Last Used</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @if (apiTokens().length === 0) {
                    <tr>
                      <td colspan="8" class="text-center text-body-secondary py-3">
                        No API tokens found
                      </td>
                    </tr>
                  }
                  @for (token of apiTokens(); track token.id) {
                    <tr>
                      <td>{{ token.id }}</td>
                      <td>
                        {{ token.user_email }}
                        <small class="text-body-secondary">(#{{ token.user_id }})</small>
                      </td>
                      <td>{{ token.created_by_email }}</td>
                      <td>{{ token.description || '-' }}</td>
                      <td>{{ formatDateTime(token.expires_at) }}</td>
                      <td>{{ token.last_used_at ? formatDateTime(token.last_used_at) : '-' }}</td>
                      <td>
                        @if (token.active) {
                          <span class="badge bg-success">Active</span>
                        }
                        @if (token.revoked_at) {
                          <span class="badge bg-danger">Revoked</span>
                        }
                        @if (!token.active && !token.revoked_at) {
                          <span class="badge bg-warning">Expired</span>
                        }
                      </td>
                      <td>
                        @if (token.active) {
                          <button
                            cButton
                            color="danger"
                            variant="ghost"
                            size="sm"
                            (click)="revokeToken(token.id)"
                          >
                            Revoke
                          </button>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>
  </c-container>
}


<!-- Generate Token Modal -->
<c-modal [visible]="generateTokenModalVisible()" (visibleChange)="generateTokenModalVisible.set($event)" backdrop="static" size="lg">
  <c-modal-header>
    <h5 cModalTitle>Generate Payout API Token</h5>
    <button type="button" cButtonClose aria-label="Close" (click)="closeGenerateTokenModal()"></button>
  </c-modal-header>
  <c-modal-body>
    @if (!generatedToken()) {
      <div class="mb-3">
        <label class="form-label">Admin User ID</label>
        <input
          type="number"
          class="form-control"
          [value]="generateTokenForm().user_id ?? ''"
          (input)="updateGenerateTokenFormField('user_id', +$any($event.target).value || null)"
          placeholder="User ID of the admin"
        />
        <small class="text-body-secondary">The admin user who will use this token. Must have admin role.</small>
      </div>
      <div class="mb-3">
        <label class="form-label">Description (optional)</label>
        <input
          type="text"
          class="form-control"
          [value]="generateTokenForm().description"
          (input)="updateGenerateTokenFormField('description', $any($event.target).value)"
          placeholder="e.g. Payout run Feb 2026"
        />
      </div>
      <div class="alert alert-info mb-0">
        Token expires after <strong>1 month</strong>. The payout code from Call 1 expires after 10 minutes.
      </div>
    } @else {
      <div class="alert alert-warning">
        <strong>Copy this token now!</strong> It will not be shown again.
      </div>
      <div class="mb-3">
        <label class="form-label">API Token</label>
        <div class="input-group">
          <input
            type="text"
            class="form-control font-monospace"
            [value]="generatedToken()"
            readonly
          />
          <button cButton color="primary" (click)="copyToken()">
            {{ tokenCopied() ? 'Copied!' : 'Copy' }}
          </button>
        </div>
      </div>
      <div class="mb-3">
        <label class="form-label">Expires At</label>
        <input type="text" class="form-control" [value]="generatedTokenExpires()" readonly />
      </div>
      <div class="mt-3">
        <label class="form-label">Usage (curl)</label>
        <pre class="bg-dark text-light p-3 rounded small" style="white-space: pre-wrap; word-break: break-all;">
          {{ getCurlExample() }}
        </pre>
      </div>
    }
  </c-modal-body>
  <c-modal-footer>
    <button cButton color="secondary" (click)="closeGenerateTokenModal()">
      {{ generatedToken() ? 'Close' : 'Cancel' }}
    </button>
    @if (!generatedToken()) {
      <button
        cButton
        color="primary"
        (click)="generateToken()"
        [disabled]="!generateTokenForm().user_id"
      >
        Generate
      </button>
    }
  </c-modal-footer>
</c-modal>

<!-- Edit User Modal -->
<c-modal [visible]="editModalVisible()" (visibleChange)="editModalVisible.set($event)" backdrop="static" size="lg">
  <c-modal-header class="d-flex justify-content-between align-items-center">
    <h5 cModalTitle class="mb-0">{{ getEditUserTitle() }}</h5>
    <button type="button" cButtonClose aria-label="Close" (click)="closeEditModal()"></button>
  </c-modal-header>

  <c-modal-body>
    <form>
      <h6 class="mb-3">{{ 'admin.users.basicInformation' | translate }}</h6>
      <c-row>
        <c-col md="6">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.firstName' | translate }}</label>
            <input
              type="text"
              class="form-control"
              [ngModel]="editForm().first_name"
              (ngModelChange)="updateEditForm({first_name: $event})"
              name="first_name"
            />
          </div>
        </c-col>
        <c-col md="6">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.lastName' | translate }}</label>
            <input
              type="text"
              class="form-control"
              [ngModel]="editForm().last_name"
              (ngModelChange)="updateEditForm({last_name: $event})"
              name="last_name"
            />
          </div>
        </c-col>
      </c-row>

      <c-row>
        <c-col md="12">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.email' | translate }}</label>
            <input
              type="email"
              class="form-control"
              [ngModel]="editForm().email"
              (ngModelChange)="updateEditForm({email: $event})"
              name="email"
            />
          </div>
        </c-col>
      </c-row>

      <h6 class="mb-3 mt-4">{{ 'admin.users.accountDetails' | translate }}</h6>
      <c-row>
        <c-col md="4">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.userType' | translate }}</label>
            <select
              class="form-select"
              [ngModel]="editForm().user_type"
              (ngModelChange)="updateEditForm({user_type: $event})"
              name="user_type"
              [disabled]="!isSuperAdmin()"
            >
              <option value="">{{ 'admin.users.selectUserType' | translate }}</option>
              <option value="user">{{ 'admin.users.userTypeUser' | translate }}</option>
              <option value="admin">{{ 'admin.users.userTypeAdmin' | translate }}</option>
              <option value="super">{{ 'admin.users.userTypeSuper' | translate }}</option>
              <option value="pool">{{ 'admin.users.userTypePool' | translate }}</option>
            </select>
            @if (!isSuperAdmin()) {
              <small class="text-body-secondary">{{ 'admin.users.userTypeRestricted' | translate }}</small>
            }
          </div>
        </c-col>
        <c-col md="4">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.sponsorId' | translate }}</label>
            <input
              type="number"
              class="form-control"
              [ngModel]="editForm().sponsor_id"
              (ngModelChange)="updateEditForm({sponsor_id: $event})"
              name="sponsor_id"
            />
          </div>
        </c-col>
        <c-col md="4">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.level' | translate }}</label>
            <input
              type="number"
              class="form-control"
              [ngModel]="editForm().level"
              (ngModelChange)="updateEditForm({level: $event})"
              name="level"
              min="0"
            />
          </div>
        </c-col>
      </c-row>

      <c-row>
        <c-col md="6">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.directCards' | translate }}</label>
            <input
              type="number"
              class="form-control"
              [ngModel]="editForm().direct_cards"
              (ngModelChange)="updateEditForm({direct_cards: $event})"
              name="direct_cards"
              min="0"
            />
          </div>
        </c-col>
        <c-col md="6" *ngIf="allowBalanceEdit">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.balance' | translate }}</label>
            <input
              type="number"
              step="0.01"
              class="form-control"
              [ngModel]="editForm().balance"
              (ngModelChange)="updateEditForm({balance: $event})"
              name="balance"
            />
          </div>
        </c-col>
      </c-row>

      <c-row>
        <c-col md="6">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.dateOfJoining' | translate }}</label>
            <input
              type="date"
              class="form-control"
              [ngModel]="editForm().date_of_joining"
              (ngModelChange)="updateEditForm({date_of_joining: $event})"
              name="date_of_joining"
            />
          </div>
        </c-col>
        <c-col md="6">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.emailConfirmed' | translate }}</label>
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                [checked]="editForm().email_confirmed"
                (change)="updateEditForm({email_confirmed: $event.target.checked})"
                name="email_confirmed"
                id="emailConfirmed"
              />
              <label class="form-check-label" for="emailConfirmed">
                {{ editForm().email_confirmed ? ('admin.users.statusYes' | translate) : ('admin.users.statusNo' | translate) }}
              </label>
            </div>
          </div>
        </c-col>
      </c-row>

      <c-row>
        <c-col md="12">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.adminFeePaidUntil' | translate }}</label>
            <input
              type="date"
              class="form-control"
              [ngModel]="editForm().admin_fee_paid_until"
              (ngModelChange)="updateEditForm({admin_fee_paid_until: $event})"
              name="admin_fee_paid_until"
            />
            <small class="text-body-secondary">{{ 'admin.users.adminFeePaidUntilHelp' | translate }}</small>
          </div>
        </c-col>
      </c-row>

      <h6 class="mb-3 mt-4">XShop</h6>
      <c-row>
        <c-col md="6">
          <div class="mb-3">
            <label class="form-label">XShop</label>
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                [checked]="editForm().xshop"
                (change)="updateEditForm({xshop: $any($event.target).checked})"
                name="xshop"
                id="xshop"
              />
              <label class="form-check-label" for="xshop">
                {{ editForm().xshop ? ('admin.users.statusYes' | translate) : ('admin.users.statusNo' | translate) }}
              </label>
            </div>
          </div>
        </c-col>
        <c-col md="6">
          <div class="mb-3">
            <label class="form-label">XShop Admin</label>
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                [checked]="editForm().xshop_admin"
                (change)="updateEditForm({xshop_admin: $any($event.target).checked})"
                name="xshop_admin"
                id="xshop_admin"
                [disabled]="!isSuperAdmin()"
              />
              <label class="form-check-label" for="xshop_admin">
                {{ editForm().xshop_admin ? ('admin.users.statusYes' | translate) : ('admin.users.statusNo' | translate) }}
              </label>
            </div>
            @if (!isSuperAdmin()) {
              <small class="text-body-secondary">{{ 'admin.users.userTypeRestricted' | translate }}</small>
            }
          </div>
        </c-col>
      </c-row>

      <h6 class="mb-3 mt-4">{{ 'admin.users.contactInformation' | translate }}</h6>
      <div class="mb-3">
        <label class="form-label">{{ 'admin.users.address' | translate }}</label>
        <input
          type="text"
          class="form-control"
          [ngModel]="editForm().address"
          (ngModelChange)="updateEditForm({address: $event})"
          name="address"
        />
      </div>
      <div class="mb-3">
        <label class="form-label">{{ 'admin.users.address2' | translate }}</label>
        <input
          type="text"
          class="form-control"
          [ngModel]="editForm().address2"
          (ngModelChange)="updateEditForm({address2: $event})"
          name="address2"
        />
      </div>

      <c-row>
        <c-col md="6">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.city' | translate }}</label>
            <input
              type="text"
              class="form-control"
              [ngModel]="editForm().city"
              (ngModelChange)="updateEditForm({city: $event})"
              name="city"
            />
          </div>
        </c-col>
        <c-col md="6">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.postcode' | translate }}</label>
            <input
              type="text"
              class="form-control"
              [ngModel]="editForm().postcode"
              (ngModelChange)="updateEditForm({postcode: $event})"
              name="postcode"
            />
          </div>
        </c-col>
      </c-row>

      <c-row>
        <c-col md="6">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.country' | translate }}</label>
            {{'test'}}
            <c-multi-select
              [ngModel]="editForm().country_code"
              (valueChange)="updateEditForm({ country_code: $event, state_code: '' })"
              [multiple]="false"
              selectionType="text"
              [placeholder]="'admin.users.selectCountry' | translate"
              [search]="true"
              [searchNoResultsLabel]="'admin.users.noCountriesFound' | translate"
              [cleaner]="true"
              name="country_code"
            >
              @for (country of countries(); track country.value) {
                <c-multi-select-option [value]="country.value">
                  {{ country.label }}
                </c-multi-select-option>
              }
            </c-multi-select>
          </div>
        </c-col>
        <c-col md="6">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.state' | translate }}</label>
            <c-multi-select
              [ngModel]="editForm().state_code"
              (valueChange)="updateEditForm({ state_code: $event })"
              [multiple]="false"
              selectionType="text"
              [placeholder]="'admin.users.selectState' | translate"
              [search]="true"
              [searchNoResultsLabel]="'admin.users.noStatesFound' | translate"
              [cleaner]="true"
              [disabled]="selectedCountryStates().length === 0"
              name="state_code"
            >
              @for (state of selectedCountryStates(); track state.value) {
                <c-multi-select-option [value]="state.value">
                  {{ state.label }}
                </c-multi-select-option>
              }
            </c-multi-select>
          </div>
        </c-col>
      </c-row>

      <div class="mb-3">
        <label class="form-label">{{ 'admin.users.mobile' | translate }}</label>
        <input
          type="tel"
          class="form-control"
          [ngModel]="editForm().mobile"
          (ngModelChange)="updateEditForm({mobile: $event})"
          name="mobile"
        />
      </div>

      <h6 class="mb-3 mt-4">{{ 'admin.users.personalInformation' | translate }}</h6>
      <c-row>
        <c-col md="4">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.dateOfBirth' | translate }}</label>
            <input
              type="date"
              class="form-control"
              [ngModel]="editForm().dob"
              (ngModelChange)="updateEditForm({dob: $event})"
              name="dob"
            />
          </div>
        </c-col>
        <c-col md="4">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.gender' | translate }}</label>
            <select
              class="form-select"
              [ngModel]="editForm().gender"
              (ngModelChange)="updateEditForm({gender: $event})"
              name="gender"
            >
              <option value="">{{ 'admin.users.selectGender' | translate }}</option>
              <option value="male">{{ 'admin.users.genderMale' | translate }}</option>
              <option value="female">{{ 'admin.users.genderFemale' | translate }}</option>
              <option value="other">{{ 'admin.users.genderOther' | translate }}</option>
            </select>
          </div>
        </c-col>
        <c-col md="4">
          <div class="mb-3">
            <label class="form-label">{{ 'admin.users.language' | translate }}</label>
            <input
              type="text"
              class="form-control"
              [ngModel]="editForm().language"
              (ngModelChange)="updateEditForm({language: $event})"
              name="language"
              [placeholder]="'admin.users.languagePlaceholder' | translate"
            />
          </div>
        </c-col>
      </c-row>
    </form>
  </c-modal-body>

  <c-modal-footer>
    <button cButton color="secondary" (click)="closeEditModal()">
      {{ 'admin.users.cancel' | translate }}
    </button>
    <button cButton color="primary" (click)="saveUser()">
      {{ 'admin.users.saveChanges' | translate }}
    </button>
  </c-modal-footer>
</c-modal>

<!-- Soft Delete Confirmation Modal -->
<c-modal [(visible)]="deleteConfirmModalVisible" backdrop="static" size="lg">
  <c-modal-header>
    <h5 cModalTitle>{{ 'admin.users.confirmDeleteTitle' | translate }}</h5>
    <button type="button" cButtonClose aria-label="Close" (click)="closeDeleteConfirmModal()"></button>
  </c-modal-header>
  <c-modal-body>
    <p>{{ getConfirmDeleteMessage() }}</p>

    <!-- Sponsor warning -->
    <div *ngIf="userToDeleteHasSponsors" class="alert alert-warning">
      <strong>{{ getConfirmDeleteSponsorWarning() }}</strong>
      <p class="mb-0 mt-2">{{ 'admin.users.confirmDeleteSponsorNote' | translate }}</p>
    </div>
  </c-modal-body>
  <c-modal-footer>
    <button cButton color="secondary" (click)="closeDeleteConfirmModal()">
      {{ 'admin.users.cancel' | translate }}
    </button>
    <button cButton color="danger" (click)="confirmSoftDelete()">
      {{ 'admin.users.confirmDelete' | translate }}
    </button>
  </c-modal-footer>
</c-modal>
